import type { VoidFunctionComponent } from 'react';

import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { GreenCheck, Icon, icons, IconText } from '../../../generic/Icon';
import Tooltip from '../../../generic/Tooltip';

import type { FossabotConnectionState } from './useFossabotConnection';

import styles from './FossabotConnectionBanner.module.less';

type Props = {
  state: FossabotConnectionState;
  /** Error detail shown in a tooltip on the badge in the error state. */
  errorMessage?: string;
  /** Connect button click (not-connected state). Wired up by a parent later. */
  onConnect?: () => void;
};

const ConnectedBody: VoidFunctionComponent = () => (
  <>
    <span className={styles.label}>
      <strong>fossabot</strong> status
    </span>
    <span className={styles.statusBadge}>
      <GreenCheck />
      Connected
    </span>
  </>
);

const CheckingBody: VoidFunctionComponent = () => (
  <>
    <span className={styles.label}>
      <strong>fossabot</strong> status
    </span>
    <span className={styles.statusBadge}>
      <span className={styles.checkingIcon}>
        <Icon icon={icons.spinner} hasSpin />
      </span>
      Checking
    </span>
  </>
);

const ErrorBody: VoidFunctionComponent<{ errorMessage?: string }> = ({ errorMessage }) => (
  <>
    <span className={styles.label}>
      <strong>fossabot</strong> status
    </span>
    <Tooltip id="fossabot-status-error" tooltip={errorMessage}>
      <span className={styles.statusBadge}>
        <span className={styles.errorIcon}>
          <Icon icon={icons.xMark} />
        </span>
        Error
      </span>
    </Tooltip>
  </>
);

const FinishingSetupBody: VoidFunctionComponent = () => (
  <>
    <span className={styles.label}>
      <strong>fossabot is finishing setup.</strong>{' '}
      This may take a few minutes...
    </span>
    <span className={styles.statusBadge}>
      <span className={styles.checkingIcon}>
        <Icon icon={icons.spinner} hasSpin />
      </span>
      Setting up
    </span>
  </>
);

const SetupNeedsAttentionBody: VoidFunctionComponent = () => (
  <>
    <span className={styles.label}>
      <strong>fossabot setup needs attention.</strong>{' '}
      Please contact your FOSSA administrator.
    </span>
    <span className={styles.statusBadge}>
      <span className={styles.attentionIcon}>
        <Icon icon={icons.warning} />
      </span>
      Action required
    </span>
  </>
);

const DisconnectedBody: VoidFunctionComponent<{ onConnect?: () => void }> = ({ onConnect }) => (
  <>
    <span className={styles.label}>
      Automate this fix with <strong>fossabot</strong>
    </span>
    <Button
      type={BUTTON_TYPE.SECONDARY}
      size={BUTTON_SIZE.MEDIUM}
      onClick={() => onConnect?.()}
    >
      {/* IconText (alignIcon="right") so the external-link icon trails the
          label; Button's own `icon` prop only renders leading. */}
      <IconText text="Connect" icon={icons.externalLink} alignIcon="right" />
    </Button>
  </>
);

const BannerBody: VoidFunctionComponent<Props> = ({
  state,
  errorMessage,
  onConnect,
}) => {
  if (state === 'connected') {
    return <ConnectedBody />;
  }
  if (state === 'checking') {
    return <CheckingBody />;
  }
  if (state === 'error') {
    return <ErrorBody errorMessage={errorMessage} />;
  }
  if (state === 'finishing-setup') {
    return <FinishingSetupBody />;
  }
  if (state === 'setup-attention-needed') {
    return <SetupNeedsAttentionBody />;
  }
  return <DisconnectedBody onConnect={onConnect} />;
};

export const FossabotConnectionBanner: VoidFunctionComponent<Props> = ({
  state,
  errorMessage,
  onConnect,
}) => (
  <div className={styles.banner}>
    <BannerBody
      state={state}
      errorMessage={errorMessage}
      onConnect={onConnect}
    />
  </div>
);
